import { create } from "zustand";
import { applyNodeChanges, applyEdgeChanges, addEdge } from "@xyflow/react";

export const MOODS = ["calm", "agitated", "hostile", "panicked"];

export const NODE_CATALOG = [
  {
    type: "trigger", label: "Triggers", color: "#2E6BFF",
    kinds: [
      { kind: "area_arrival", label: "On Area Arrival", defaults: { zone: "Sector 4" } },
      { kind: "shots_fired", label: "Shots Fired", defaults: { threshold: 1 } },
      { kind: "call_911", label: "911 Call Received", defaults: { priority: "Priority 1" } },
      { kind: "time_elapsed", label: "Time Elapsed", defaults: { seconds: 60 } },
    ],
  },
  {
    type: "logic", label: "Logic & Branching", color: "#a78bfa",
    kinds: [
      { kind: "escalation_chance", label: "Escalation Chance", defaults: { chance: 50 } },
      { kind: "mood_check", label: "Mood Check", defaults: { mood: "hostile" } },
      { kind: "sightline_check", label: "Suspect Sightline Check", defaults: { range: 30 } },
    ],
  },
  {
    type: "profile", label: "AI Behavioral Profile", color: "#FF6B1A",
    kinds: [
      { kind: "suspect_profile", label: "Suspect Parameters", defaults: { mood: "agitated", compliance: 50, negotiation_resistance: 1.0 } },
    ],
  },
  {
    type: "action", label: "Actions", color: "#22c55e",
    kinds: [
      { kind: "spawn_swat", label: "Spawn SWAT", defaults: { units: 4, staging: "Staging A" } },
      { kind: "trigger_barricade", label: "Trigger Barricade", defaults: { location: "Main entrance" } },
      { kind: "radio_broadcast", label: "Radio Dispatch Broadcast", defaults: { message: "All units, respond Code 3." } },
      { kind: "set_hostage_state", label: "Set Hostage State", defaults: { state: "captive" } },
    ],
  },
];

export const kindMeta = (type, kind) => {
  const cat = NODE_CATALOG.find((c) => c.type === type);
  return { cat, meta: cat?.kinds.find((k) => k.kind === kind) };
};

let counter = 1;
export const nextNodeId = () => `n${Date.now().toString(36)}${counter++}`;

const emptyMission = () => ({
  id: null, name: "Untitled Blueprint", description: "", scenario: "active_shooter",
  service_tag: "police", difficulty: "Moderate", shared: false, ancestor_id: null,
});

export const useSdkStore = create((set, get) => ({
  nodes: [], edges: [], selectedId: null,
  mission: emptyMission(),

  setMissionMeta: (patch) => set({ mission: { ...get().mission, ...patch } }),
  resetMission: () => set({ mission: emptyMission(), nodes: [], edges: [], selectedId: null }),

  loadMission: (m) => set({
    mission: { ...emptyMission(), ...m },
    nodes: (m.graph?.nodes || []).map((n) => ({ ...n })),
    edges: (m.graph?.edges || []).map((e) => ({ ...e })),
    selectedId: null,
  }),

  onNodesChange: (changes) => {
    const nodes = applyNodeChanges(changes, get().nodes);
    const sel = nodes.find((n) => n.selected);
    set({ nodes, selectedId: sel ? sel.id : get().selectedId && nodes.some((n) => n.id === get().selectedId) ? get().selectedId : null });
  },
  onEdgesChange: (changes) => set({ edges: applyEdgeChanges(changes, get().edges) }),
  onConnect: (conn) => set({ edges: addEdge({ ...conn, animated: true, style: { stroke: "#52525b" } }, get().edges) }),

  addNode: (type, kind, position) => {
    const { meta } = kindMeta(type, kind);
    const node = {
      id: nextNodeId(), type, position,
      data: { kind, label: meta?.label || kind, ...(meta?.defaults || {}) },
    };
    set({ nodes: [...get().nodes, node] });
    return node;
  },

  updateNodeData: (id, patch) => set({
    nodes: get().nodes.map((n) => (n.id === id ? { ...n, data: { ...n.data, ...patch } } : n)),
  }),

  graphPayload: () => ({
    nodes: get().nodes.map(({ id, type, position, data }) => ({ id, type, position: { x: Math.round(position.x), y: Math.round(position.y) }, data })),
    edges: get().edges.map(({ id, source, target, sourceHandle }) => ({ id, source, target, sourceHandle: sourceHandle || null })),
  }),

  // ---- simulation state (consumed by Sandbox + CAD modules) ----
  sim: { time: 0, playing: false, speed: 1 },
  setSim: (patch) => set({ sim: { ...get().sim, ...patch } }),
}));
